import { useCallback, useEffect, useState } from 'react'
import { Button, DatePicker, Divider, Form, Input, InputNumber, Modal, Select, Space, Table, TableProps, Tag } from 'antd'
import { PlusOutlined, MinusOutlined, LockOutlined, EyeOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons"
import CustomTable from '../../components/table'
import defaultParams, { FORMAT_DATE, TRANSACTION_CONNECTION_TYPE_CONNECTION, TRANSACTION_CONNECTION_TYPE_EXTENSION } from '../../assets/constants/default_params'
import { apiAddListConditionRule, apiAddListPackageDiscount, apiDeletePartnerPackage, apiGetListCompanies, apiGetListConditionRule, apiGetListPackageDiscount, apiGetListPackages, apiGetListPartnerPackages, apiUpdateListPackageDiscount, apiUpdatePartnerPackage } from '../../api'
import { useLoading } from '../../context/LoadingContext'
import { IPackage, IPartnerPackage, IPartnerPackages } from '../../models/package.modal'
import { Link } from 'react-router-dom';
import { PACKAGE, CREATE, DETAIL } from '../../assets/constants/route_path'
import { toastError, toastSuccess } from '../../utils/toast'
import { formatCurrency } from '../../utils/string-utils'
import FormSearch from '../../components/form'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'
import { ICompany } from '../../models/company.modal'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { formatDate } from '../../utils/entity-utils'
import { partnerPackageTypeOptions } from '../../assets/constants/default_options'
import { renderTypePackages } from './ViewOverview'
import moment from 'moment'
import dayjs from 'dayjs'

export const renderPackageType = (type: string) => {
    const style: object = { width: 100, textAlign: 'center', marginRight: 0 }
    if (type === 'CONNECTION') return <Tag style={style} color="green">Đấu mới</Tag>
    if (type === 'EXTENSION') return <Tag style={style} color="purple">Gia hạn</Tag>
    if (type === '3G') return <Tag style={style} color="red">API gói</Tag>
    if (type === 'ADDON') return <Tag style={style} color="blue">Data sponsor</Tag>
    return
}

export const renderDiscount = (priceType: string, discountType: string, discountValue: string) => {
    let discount = ''
    switch (priceType) {
        case 'SELLING':
            discount = 'Giá bán'
            break
        case 'CASHBACK':
            discount = 'Trả lại'
            break
        case 'DISCOUNT':
            discount = 'Hoa hồng Viettel'
            break
        case 'ORIGINAL':
            discount = 'Giá gốc'
            break
    }
    if (Number(discountValue) < 0) {
        discount += ' tăng '
    } else {
        discount += ' giảm '
    }
    switch (discountType) {
        case 'FIXED':
            discount += formatCurrency(Math.abs(Number(discountValue)))
            break;
        case 'PERCENT':
            discount += Math.abs(Number(discountValue)) + '%'
            break;
    }
    return discount
}

export const calculateProfit = (pack: any) => {
    return ((pack?.price || 0) + (pack?.price_discount || 0)) - ((pack?.package?.price || 0) + (pack?.price_cashback || 0)) - (pack?.price_expense || 0)
}

export default function PackagePartner() {
    const [packageOptions, setPackageOptions] = useState<{ label: string, value: Number }[]>([])
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [companyValue, setCompanyValue] = useState({
        id: 0,
        name: '',
        code: '',
    })
    const [packages, setPackages] = useState<IPartnerPackages>({})
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [search, setSearch] = useState({
        name: '',
        code: '',
        price: 0,
        company: '',
        type: '',
    })
    const [modalDelete, setModalDelete] = useState<{ data: IPartnerPackage | null, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })
    const [modalView, setModalView] = useState<{ data: any, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })
    const [modalDisabled, setModalDisabled] = useState<{ data: any, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })
    const [showAddDiscount, setShowAddDiscount] = useState(false)
    const initCondition = { id: Date.now(), description: "", field: "", operator: "", value: "" }
    const [conditions, setConditions] = useState([initCondition])
    const [modalUpdate, setModalUpdate] = useState<{ data: any, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })

    const { setIsLoading } = useLoading()
    const authState = useSelector(authSelector)
    const { account } = authState
    const [formSearch] = Form.useForm()
    const [formCreate] = Form.useForm()
    const [formUpdate] = Form.useForm()

    const columns: TableProps<any>['columns'] = [
        {
            key: 'STT',
            title: 'STT',
            render: (value, record, index) => ++index + (pageSize * (currentPage - 1)),
        },
        {
            key: 'name',
            title: 'Tên gói cước',
            dataIndex: 'name',
        },
        {
            key: 'code',
            title: 'Mã gói cước',
            dataIndex: 'code',
        },
        {
            key: 'price_selling',
            title: 'Giá bán',
            dataIndex: 'price',
            sorter: (a, b) => (a.price || 0) - (b.price || 0),
            render: (price) => <span style={{ whiteSpace: 'nowrap', color: 'green' }}>{formatCurrency(price || 0)}</span>,
        },
        {
            key: 'price_discount',
            title: 'Hoa hồng Viettel',
            dataIndex: 'price_discount',
            sorter: (a, b) => (a.price_discount || 0) - (b.price_discount || 0),
            render: (price_discount) => price_discount ? <span style={{ whiteSpace: 'nowrap', color: 'green' }}>{formatCurrency(price_discount || 0)}</span> : '-',
        },
        {
            key: 'price_original',
            title: 'Giá gốc',
            dataIndex: 'package',
            sorter: (a, b) => (a.package?.price || 0) - (b.package?.price || 0),
            render: (pack: any) => <span style={{ whiteSpace: 'nowrap', color: 'red' }}>{formatCurrency(pack?.price || 0)}</span>,
        },
        {
            key: 'price',
            title: 'Trả lại',
            dataIndex: 'price_cashback',
            sorter: (a, b) => (a.price_cashback || 0) - (b.price_cashback || 0),
            render: (price_cashback) => price_cashback ? <span style={{ whiteSpace: 'nowrap', color: 'red' }}>{formatCurrency(price_cashback || 0)}</span> : '-',
        },
        {
            key: 'price_expense',
            title: 'Chi phí',
            dataIndex: 'price_expense',
            sorter: (a, b) => (a.price_expense || 0) - (b.price_expense || 0),
            render: (price_expense) => price_expense ? <span style={{ whiteSpace: 'nowrap', color: 'red' }}>{formatCurrency(price_expense || 0)}</span> : '-',
        },
        {
            key: 'price_profit',
            title: 'Lợi nhuận',
            sorter: (a, b) => (calculateProfit(a) || 0) - (calculateProfit(b) || 0),
            render: (data) => <span style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>{formatCurrency(calculateProfit(data) || 0)}</span>,
        },
        {
            key: 'company',
            title: 'Công ty',
            sorter: (a, b) => (a.company?.name || '').localeCompare(b.company?.name || ''),
            render: (_, data: IPartnerPackage) => data.company?.name || Number(data?.company_id),
        },
        {
            key: 'package',
            title: 'Gói cước',
            sorter: (a, b) => (a.package?.name || '').localeCompare(b.package?.name || ''),
            render: (_, data: IPartnerPackage) => data.package?.name || Number(data?.package_id),
        },
        {
            key: 'type',
            title: 'Loại gói cước',
            dataIndex: 'type',
            sorter: (a, b) => (a.type || '').localeCompare(b.type || ''),
            render: (type) => renderPackageType(type),
        },
        {
            key: 'modify',
            title: 'Cập nhật',
            dataIndex: 'modify',
            render: (modify) => formatDate(modify),
        },
        {
            key: 'action',
            title: 'Tác vụ',
            dataIndex: 'action',
            align: 'center',
            render: (_, data: IPartnerPackage) => (
                <Space>
                    <Button icon={<EyeOutlined />} onClick={() => setModalView({ data, isOpen: true })}
                        style={{ background: '#3e64b5', color: 'white' }}>
                    </Button>
                    {account.canUpdatePartnerPackage && (
                        <Button onClick={() => {
                            setModalUpdate({ data, isOpen: true })
                            formUpdate.setFieldsValue(data)
                        }}
                            icon={<EditOutlined />}
                            style={{ background: '#c48912', color: 'white' }}>
                        </Button>
                    )}
                    {account.canDeletePartnerPackage && (
                        <Button onClick={() => setModalDelete({ data, isOpen: true })}
                            icon={<DeleteOutlined />} type='primary' danger>
                        </Button>
                    )}
                </Space>
            ),
        },
    ]

    const discountColumns: TableProps<any>['columns'] = [
        {
            key: 'discount',
            title: 'Khuyến mãi',
            render: data => renderDiscount(data.price_type, data.discount_type, data.discount_value)
        },
        {
            key: 'condition',
            title: 'Điều kiện',
            dataIndex: 'conditions',
            render: conditions => conditions.map((item: any) => <Tag color='red'>{item.description}</Tag>)
        },
        {
            key: 'action',
            title: 'Tác vụ',
            render: data => (
                <Button
                    disabled={data.disabled}
                    onClick={() => setModalDisabled({ isOpen: true, data })}
                    icon={<LockOutlined />} size='small' danger>
                    Khoá
                </Button>
            )
        }
    ]

    const fetchCompanies = useCallback(async () => {
        try {
            const resCompanies = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            if (resCompanies.data != null) {
                const options = resCompanies.data.companies.map((item: ICompany) => ({
                    label: `${item.id} - ${item.name}`,
                    value: `${item.id}::${item.name}::${item.code}`
                }))
                setCompanyOptions(options)
            } else {
                setCompanyOptions([])
            }
        } catch (error) {
            console.log(error)
        }
    }, [])

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const resp = await apiGetListPartnerPackages({
                limit: pageSize,
                offset: pageSize * (currentPage - 1),
                name: search.name,
                code: search.code,
                price: search.price,
                company_id: Number(search.company?.split('::')[0]) || 0,
                type: search.type,
            })
            if (resp.data === null) {
                setPackages({})
                return
            }
            setPackages(resp.data)
            handleMappingPartnerPackages(resp.data)
            setPackageOptions(resp.data.partner_packages
                .filter((item: IPartnerPackage) =>
                    item.type === TRANSACTION_CONNECTION_TYPE_CONNECTION ||
                    item.type === TRANSACTION_CONNECTION_TYPE_EXTENSION)
                .map((item: IPartnerPackage) => {
                    let label = item.name
                    let value = `${item.cpCode}.${item.code}.${item.type}`
                    if (item.type === TRANSACTION_CONNECTION_TYPE_CONNECTION) {
                        label += '_Đấu mới'
                    }
                    if (item.type === TRANSACTION_CONNECTION_TYPE_EXTENSION) {
                        label += '_Gia hạn'
                    }
                    return { label, value }
                }))
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, currentPage, pageSize, search])

    const handleMappingPartnerPackages = async (respPartner: any) => {
        try {
            const [respPackages, respCompanies, respDiscount, respCondition]: any = await Promise.all([
                apiGetListPackages({ limit: defaultParams.MAX_LIMIT }),
                apiGetListCompanies({ limit: defaultParams.MAX_LIMIT }),
                apiGetListPackageDiscount({ page: defaultParams.PAGE, size: defaultParams.MAX_LIMIT }),
                apiGetListConditionRule({ page: defaultParams.PAGE, size: defaultParams.MAX_LIMIT }),
            ])
            if (respPackages.data === null && respCompanies.data === null) {
                return
            }
            const resp = { count: respPartner.count, partner_packages: [] as any }
            const listPackagePartner = respPartner.partner_packages
            const listCompanies = respCompanies.data.companies
            const listPackages = respPackages.data.packages
            const listConditions = respCondition.data.conditionRule
            const listDiscounts = respDiscount.data.packageDiscount.map((discount: any) => ({
                ...discount,
                conditions: listConditions.filter((condition: any) => condition.package_discount_id === discount.id)
            }))
            for (let i = 0; i < listPackagePartner.length; i++) {
                const packagePartnerInfo = listPackagePartner[i]
                const companyInfo = listCompanies.find((company: ICompany) => company.id === packagePartnerInfo.company_id)
                const packageInfo = listPackages.find((pack: IPackage) => pack.id === packagePartnerInfo.package_id)
                const discountInfo = listDiscounts.filter((discount: any) => discount.partner_package_id === packagePartnerInfo.id)
                resp.partner_packages.push({
                    ...packagePartnerInfo,
                    company: companyInfo,
                    package: packageInfo,
                    discounts: discountInfo,
                })
            }
            setPackages(resp)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        fetchData()
        fetchCompanies()
    }, [fetchData, fetchCompanies])

    const handleSearch = ({ name, code, price, company, type }: any) => {
        if (name || code || price || company || type) {
            setSearch({ name, code, price, company, type })
            setCurrentPage(defaultParams.PAGE)
            setPageSize(defaultParams.LIMIT)
        }
    }

    const handleClearSearch = () => {
        setCompanyValue({
            id: 0,
            name: '',
            code: '',
        })
        formSearch.resetFields()
        setSearch({
            name: '',
            code: '',
            price: 0,
            company: '',
            type: '',
        })
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleDelete = async () => {
        try {
            setIsLoading(true)
            const resp = await apiDeletePartnerPackage(modalDelete.data?.id)
            if (resp.status === 200) {
                setModalDelete({ data: null, isOpen: false })
                toastSuccess('Xoá gói cước thành công!')
                fetchData()
            } else {
                toastError('Có lỗi xảy ra!')
            }
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleSetCompanyValue = (value: string) => {
        const [id, name, code] = value?.split('::')
        setCompanyValue({ id: Number(id), name, code })
    }

    const handleClickAddPackage = (e: any) => {
        if (!companyValue.id || !companyValue.name || !companyValue.code) {
            e.preventDefault()
            toastError('Vui lòng chọn công ty!')
        }
    }

    const handleChange = (id: any, key: any, value: any) => {
        setConditions((prev) => prev.map((cond) => (cond.id === id ? { ...cond, [key]: value } : cond)))
    }

    const handleAddDiscount = async (values: any) => {
        try {
            setIsLoading(true)
            for (let i = 0; i < conditions.length; i++) {
                const condition = conditions[i]
                if (!condition.field || !condition.operator || !condition.value || !condition.description) {
                    toastError('Điều khiện không hợp lệ!')
                    return
                }
            }
            const { data: discount } = await apiAddListPackageDiscount({
                packageDiscount: [{
                    partner_package_id: modalView.data.id,
                    price_type: values.price_type,
                    discount_type: values.discount_type,
                    discount_value: values.discount_value,
                }]
            })
            const { data: condition } = await apiAddListConditionRule({
                conditionRule: conditions.map((item: any) => {
                    switch (item.field) {
                        case 'TIME':
                            item.value = moment(new Date(item.value)).unix().toString()
                            break
                    }
                    return {
                        package_discount_id: discount.packageDiscount[0].id,
                        field: item.field,
                        operator: item.operator,
                        value: item.value,
                        description: item.description,
                    }
                })
            })
            modalView.data.discounts.push({
                ...discount.packageDiscount[0],
                conditions: condition.conditionRule
            })
            fetchData()
            setConditions([initCondition])
            formCreate.resetFields()
            setShowAddDiscount(false)
            toastSuccess('Thêm khuyến mãi thành công!')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleDeleteDiscount = async () => {
        try {
            setIsLoading(true)
            await apiUpdateListPackageDiscount({
                packageDiscount: [{
                    id: modalDisabled.data.id,
                    disabled: true
                }]
            })
            modalView.data.discounts = modalView.data.discounts.map((item: any) => {
                if (item.id === modalDisabled.data.id) {
                    item.disabled = true
                }
                return item
            })
            setModalDisabled({ data: null, isOpen: false })
            toastSuccess('Xoá khuyễn mãi thành công!')
            fetchData()
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const renderConditionValue = (cond: any) => {
        switch (cond.field) {
            case 'TIME':
                return (
                    <DatePicker
                        value={cond.value}
                        onChange={value => handleChange(cond.id, "value", dayjs(value, FORMAT_DATE))}
                        style={{ width: 200 }}
                    />
                )
            case 'DATA_PACK':
                return (
                    <Select
                        value={cond.value}
                        onChange={value => handleChange(cond.id, "value", value)}
                        style={{ width: 200 }}
                        options={packageOptions}
                    />
                )
            default:
                return (
                    <Input
                        value={cond.value}
                        onChange={(e) => handleChange(cond.id, "value", e.target.value)}
                        style={{ width: 200 }}
                    />
                )
        }
    }

    const handleUpdate = async (values: any) => {
        try {
            setIsLoading(true)
            const resp = await apiUpdatePartnerPackage(values)
            if (resp.status === 200) {
                setModalUpdate({ data: null, isOpen: false })
                toastSuccess('Cập nhật gói cước thành công!')
                fetchData()
            } else {
                toastError('Có lỗi xảy ra!')
            }
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <p style={{ marginBottom: 20, textAlign: 'end' }}>
                <span style={{ color: 'red' }}>Lợi nhuận: </span>
                <span> (Giá bán + Hoa hồng Viettel) - (Giá gốc + Trả lại) - Chi phí</span>
            </p>
            <FormSearch form={formSearch} handleSearch={handleSearch} handleClearSearch={handleClearSearch}>
                <Form.Item name='company' label='Công ty'>
                    <Select
                        onChange={handleSetCompanyValue}
                        style={{ minWidth: 170 }}
                        showSearch
                        placeholder="Chọn công ty"
                        options={companyOptions}
                        filterOption={filterOptionByLabel}
                    />
                </Form.Item>
                <Form.Item name='name' label='Tên gói cước'>
                    <Input />
                </Form.Item>
                <Form.Item name='code' label='Mã gói cước'>
                    <Input />
                </Form.Item>
                <Form.Item name='price' label='Giá bán'>
                    <InputNumber formatter={value => formatCurrency(Number(value))} style={{ minWidth: 170 }} />
                </Form.Item>
                <Form.Item name='type' label='Loại gói cước'>
                    <Select style={{ width: 170 }} options={partnerPackageTypeOptions} placeholder="Chọn loại gói cước" />
                </Form.Item>
            </FormSearch>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Space>Đối tác:{companyValue.name && <h3>{companyValue.name}</h3>}</Space>
                {account.canAddListPartnerPackages && (
                    <div style={{ textAlign: 'end', marginBottom: 20 }}>
                        <Link to={PACKAGE + DETAIL + CREATE + `?id=${companyValue.id}&name=${companyValue.name}&code=${companyValue.code}`}>
                            <Button onClick={handleClickAddPackage} type='primary'>Thêm gói cước</Button>
                        </Link>
                    </div>
                )}
            </div>
            <CustomTable
                namePage='gói cước'
                columns={columns}
                dataSource={packages.partner_packages}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={packages.count}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal title='Xoá gói cước' open={modalDelete.isOpen}
                onCancel={() => setModalDelete({ data: null, isOpen: false })}
                footer={[
                    <Button onClick={handleDelete} type='primary' danger>
                        Xoá
                    </Button>,
                ]}>
                <Divider />
                <div>Bạn có chắc muốn xoá gói cước <b>{modalDelete.data?.name}</b>?</div>
            </Modal>
            <Modal
                className='modal-detail-partner'
                title='Chi tiết gói cước'
                open={modalView.isOpen}
                footer={[]}
                onCancel={() => {
                    setModalView({ data: null, isOpen: false })
                    setShowAddDiscount(false)
                    formCreate.resetFields()
                }}>
                <Divider />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Space>
                        <h3 style={{ margin: 0 }}>Gói cước đối tác:</h3>
                        <span>{modalView.data?.company?.name}</span>
                    </Space>
                    {account?.canAddListPackageDiscount && account?.canAddListConditionRule && (
                        <Button onClick={() => setShowAddDiscount(!showAddDiscount)}>
                            Thêm KM
                        </Button>
                    )}
                </div>
                {showAddDiscount && (
                    <Form form={formCreate} onFinish={handleAddDiscount} layout="vertical" autoComplete="off" style={{ marginBottom: 40 }}>
                        <div style={{ display: 'flex', gap: 20 }}>
                            <Form.Item style={{ flex: 1 }} name='price_type' label='Loại giá giảm'
                                rules={[{ required: true, message: 'Vui lòng chọn loại giá giảm' }]}>
                                <Select
                                    options={[
                                        { label: 'Giá bán', value: 'SELLING' },
                                        { label: 'Trả lại', value: 'CASHBACK' },
                                        { label: 'Hoa hồng Viettel', value: 'DISCOUNT' },
                                        { label: 'Giá gốc', value: 'ORIGINAL' },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item style={{ flex: 1 }} name='discount_type' label='Loại giảm giá'
                                rules={[{ required: true, message: 'Vui lòng chọn loại giảm giá' }]}>
                                <Select
                                    options={[
                                        { label: 'Giá cụ thể', value: 'FIXED' },
                                        { label: 'Phần trăm', value: 'PERCENT' },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item style={{ flex: 1 }} name='discount_value' label='Giá trị'
                                rules={[{ required: true, message: 'Vui lòng nhập giá!' }]}>
                                <InputNumber style={{ width: '100%' }} />
                            </Form.Item>
                        </div>
                        {conditions.map((cond) => (
                            <div key={cond.id} style={{ display: 'flex', gap: 20 }}>
                                <Form.Item style={{ flex: 1 }} label='Điều kiện áp dụng'>
                                    <Input
                                        value={cond.description}
                                        onChange={(e) => handleChange(cond.id, "description", e.target.value)}
                                        style={{ width: 160 }}
                                    />
                                </Form.Item>
                                <Form.Item style={{ flex: 1 }} label='Field'>
                                    <Select
                                        value={cond.field}
                                        onChange={(value) => {
                                            handleChange(cond.id, "field", value)
                                            handleChange(cond.id, "value", "")
                                        }}
                                        options={[
                                            { label: 'Thời gian', value: 'TIME' },
                                            { label: 'Gia hạn', value: 'EXTENSION' },
                                            { label: 'Msisdn', value: 'PHONE_SIM' },
                                            { label: 'Serial Sim', value: 'SERIAL_SIM' },
                                            { label: 'Gói cước', value: 'DATA_PACK' },
                                        ]}
                                    />
                                </Form.Item>
                                <Form.Item style={{ flex: 0.5 }} label='Operator'>
                                    <Select
                                        value={cond.operator}
                                        onChange={(value) => handleChange(cond.id, "operator", value)}
                                        options={[
                                            { label: '>=', value: '>=' },
                                            { label: '>', value: '>' },
                                            { label: '==', value: '==' },
                                            { label: '<=', value: '<=' },
                                            { label: '<', value: '<' },
                                        ]}
                                    />
                                </Form.Item>
                                <Form.Item style={{ flex: 2 }} label='Value'>
                                    {renderConditionValue(cond)}
                                </Form.Item>
                                <Form.Item label=' '>
                                    <Button
                                        icon={<PlusOutlined />}
                                        onClick={() => setConditions((prev) => [...prev, initCondition])}
                                        type="dashed"
                                    />
                                </Form.Item>
                                <Form.Item label=' '>
                                    {conditions.length > 1 && (
                                        <Button
                                            icon={<MinusOutlined />}
                                            onClick={() => setConditions((prev) => prev.filter((item) => item.id !== cond.id))}
                                            danger
                                            type="dashed"
                                        />
                                    )}
                                </Form.Item>
                            </div>
                        ))}
                        <div style={{ textAlign: 'end' }}>
                            <Button htmlType="submit" type='primary'>
                                Thêm
                            </Button>
                        </div>
                    </Form>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                        <p><b>Tên gói: </b>{modalView.data?.name}</p>
                        <p><b>Giá bán: </b>{formatCurrency(modalView.data?.price)}</p>
                        <p><b>Chi phí: </b>{formatCurrency(modalView.data?.price_expense)}</p>
                    </div>
                    <div>
                        <p><b>Mã gói: </b>{modalView.data?.code}</p>
                        <p><b>Trả lại: </b>{formatCurrency(modalView.data?.price_cashback)}</p>
                        <p><b>Lợi nhuận: </b>{formatCurrency(calculateProfit(modalView.data))}</p>
                    </div>
                    <div>
                        <p><b>Cập nhật: </b>{formatDate(modalView.data?.modify)}</p>
                        <p><b>Hoa hồng Viettel: </b>{formatCurrency(modalView.data?.price_discount)}</p>
                        <p><b>Network: </b>{modalView.data?.network}</p>
                    </div>
                </div>
                <div><b>Mô tả: </b>{modalView.data?.description}</div>
                {modalView.data?.discounts?.length > 0 && (
                    <div style={{ padding: 20 }}>
                        <Table
                            key={modalView.data?.discounts?.length}
                            size='small'
                            rowKey={(_, index: any) => index}
                            style={{ overflow: 'auto' }}
                            columns={discountColumns}
                            dataSource={modalView.data?.discounts}
                            pagination={false}
                        />
                    </div>
                )}
                <Divider />
                <h3 style={{ margin: 0 }}>Gói cước gốc</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                        <p><b>Tên gói: </b>{modalView.data?.package?.name}</p>
                        <p><b>Mã gói: </b>{modalView.data?.package?.code}</p>
                        <p><b>Chu kỳ tính cước: </b>{renderTypePackages(modalView.data?.package?.type)}</p>
                    </div>
                    <div>
                        <p><b>Giá gốc: </b>{formatCurrency(modalView.data?.package?.price)}</p>
                        <p><b>Cập nhật: </b>{formatDate(modalView.data?.package?.modify)}</p>
                    </div>
                </div>
                <div><b>Mô tả: </b>{modalView.data?.package?.description}</div>
                <p><b>Ghi chú: </b>{modalView.data?.package?.note}</p>
            </Modal>
            <Modal title='Xoá khuyến mãi' open={modalDisabled.isOpen}
                onCancel={() => setModalDisabled({ data: null, isOpen: false })}
                footer={[
                    <Button onClick={handleDeleteDiscount} type='primary' danger>
                        Xoá
                    </Button>,
                ]}>
                <Divider />
                <div>Bạn có chắc muốn xoá khuyến mãi <b>{modalDisabled.data?.name}</b>?</div>
            </Modal>
            <Modal
                className='modal-detail-partner'
                title='Cập nhật gói cước'
                open={modalUpdate.isOpen}
                onCancel={() => setModalUpdate({ data: null, isOpen: false })}
                footer={[]}
            >
                <Divider />
                <Form form={formUpdate} onFinish={handleUpdate} layout="vertical" autoComplete="off">
                    <Form.Item style={{ display: 'none' }} name='id' />
                    <div style={{ display: 'flex', gap: 20 }}>
                        <Form.Item style={{ flex: 1 }} name='name' label='Tên gói cước'
                            rules={[{ required: true, message: 'Vui lòng nhập tên gói cước!' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item style={{ flex: 1 }} name='code' label='Mã gói cước'
                            rules={[{ required: true, message: 'Vui lòng nhập mã gói cước!' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item style={{ flex: 1 }} name='type' label='Loại gói cước'
                            rules={[{ required: true, message: 'Vui lòng chọn loại gói cước!' }]}>
                            <Select options={partnerPackageTypeOptions} placeholder="Chọn loại gói cước" />
                        </Form.Item>
                    </div>
                    <div style={{ display: 'flex', gap: 20 }}>
                        <Form.Item style={{ flex: 1 }} name='price' label='Giá bán'
                            rules={[{ required: true, message: 'Vui lòng nhập giá bán!' }]}>
                            <InputNumber formatter={value => formatCurrency(Number(value))} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item style={{ flex: 1 }} name='price_cashback' label='Trả lại'>
                            <InputNumber formatter={value => formatCurrency(Number(value))} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item style={{ flex: 1 }} name='price_discount' label='Hoa hồng Viettel'>
                            <InputNumber formatter={value => formatCurrency(Number(value))} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item style={{ flex: 1 }} name='price_expense' label='Chi phí'>
                            <InputNumber formatter={value => formatCurrency(Number(value))} style={{ width: '100%' }} />
                        </Form.Item>
                    </div>
                    <div style={{ display: 'flex', gap: 20 }}>
                        <Form.Item style={{ flex: 2 }} name='description' label='Mô tả'>
                            <Input.TextArea />
                        </Form.Item>
                        <Form.Item style={{ flex: 1 }} name='network' label='Network'>
                            <Input />
                        </Form.Item>
                    </div>
                    <div style={{ textAlign: 'end' }}>
                        <Space>
                            <Button htmlType='submit' type='primary' icon={<EditOutlined />}>
                                <b>Cập nhật</b>
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </div>
    )
}
